import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { DialogClose } from '@/components/DialogClose';
import { SignupShell } from './Shell';

/** Terms of service and privacy policy: a popup on login/signup, and public pages. */

export type LegalKind = 'terms' | 'privacy';

type Section = { h: string; p: string[] };

const UPDATED = '26 Oct 2026';

const TERMS: Section[] = [
  { h: '1. Acceptance', p: [
    'These terms govern your use of uKnowva Payroll (the "Service"). By creating a workspace or using the Service you agree to them on behalf of the company you represent ("Customer"). If you do not have authority to bind that company, do not use the Service.',
  ] },
  { h: '2. Workspace and accounts', p: [
    'Each Customer gets a separate workspace identified by its workspace ID. The person who signs up becomes the first administrator and controls who else gets access.',
    'You must keep login details confidential, use strong passwords and remove access for people who leave. You are responsible for all activity under your workspace accounts.',
  ] },
  { h: '3. What the Service does', p: [
    'The Service calculates salaries, deductions, statutory contributions (such as PF, ESI, PT, LWF and TDS), arrears, reimbursements and related reports from the data and rules configured by the Customer.',
    'Results depend on the accuracy of the employee, attendance, salary structure and tax data you provide. You must review and approve every payroll run before payment or filing. The Service is a tool and does not give legal, tax or accounting advice.',
  ] },
  { h: '4. Customer responsibilities', p: [
    'You are responsible for the accuracy and lawful collection of the data you enter, for obtaining any consent or notice your employees need, for timely deposit of taxes and contributions, and for statutory filings made with figures from the Service.',
    'You must not upload unlawful content, try to access other workspaces, probe or disrupt the Service, or use it to process data you have no right to process.',
  ] },
  { h: '5. Customer data', p: [
    'Customer data (employee records, payroll figures, documents and reports) belongs to the Customer. We process it only to provide, secure and support the Service and as instructed by the Customer.',
    'For personal data of employees the Customer acts as the data fiduciary (controller) and we act as data processor under the Digital Personal Data Protection Act, 2023 and other applicable law. Our handling of personal data is described in the Privacy Policy.',
  ] },
  { h: '6. Security and confidentiality', p: [
    'We use industry standard safeguards, including encrypted connections, access controls by role, separate databases per workspace, audit trails and regular backups.',
    'Each party will keep the other party\'s confidential information secret and use it only for the purpose of this agreement.',
  ] },
  { h: '7. Fees', p: [
    'Fees, billing cycle and taxes are set out in your order form or plan. Unpaid fees may lead to suspension after notice. Fees are exclusive of GST, which is charged as applicable.',
  ] },
  { h: '8. Availability and support', p: [
    'We aim to keep the Service available at all times except for planned maintenance, which we try to schedule outside payroll processing peaks and announce in advance. Support levels are as stated in your plan.',
  ] },
  { h: '9. Integrations', p: [
    'The Service can connect with uKnowva HRMS, banks and other third-party systems at your request. Your use of a third-party system is governed by that provider\'s terms, and we are not responsible for its availability or accuracy.',
  ] },
  { h: '10. Intellectual property', p: [
    'We own the Service, its software and documentation. You get a non-exclusive, non-transferable right to use it for your internal business during the subscription. You keep all rights in your data and grant us only the rights needed to run the Service.',
  ] },
  { h: '11. Suspension and termination', p: [
    'Either party may end the subscription as set out in the order form, or immediately on a material breach that is not cured within 30 days of notice. We may suspend access to stop a security threat or misuse.',
    'After termination you can export your data for 30 days. After that we delete it from active systems, except where law requires us to keep it, and from backups within their normal cycle.',
  ] },
  { h: '12. Warranties and liability', p: [
    'The Service is provided with reasonable skill and care but without any other warranty, express or implied. We do not warrant that it will be error free or that calculations meet every rule that applies to you.',
    'Neither party is liable for indirect or consequential loss, lost profits or lost data beyond restoring from backup. Our total liability in any 12 months is limited to the fees paid by the Customer in that period. These limits do not apply to fraud, wilful misconduct or breach of confidentiality.',
  ] },
  { h: '13. Indemnity', p: [
    'The Customer will indemnify us against claims arising from data it uploads without the right to do so, or from its breach of law in using the Service. We will indemnify the Customer against claims that the Service infringes a third party\'s intellectual property.',
  ] },
  { h: '14. Changes', p: [
    'We may update these terms. We will give notice of material changes at least 30 days in advance through the Service or by email. Continued use after the change takes effect means acceptance.',
  ] },
  { h: '15. Governing law', p: [
    'These terms are governed by the laws of India. Disputes will first be discussed in good faith, and failing that will be subject to the courts at the place of our registered office.',
  ] },
];

const PRIVACY: Section[] = [
  { h: '1. Who we are', p: [
    'This policy explains how uKnowva Payroll collects and uses personal data. For workspace signups and account management we decide how data is used. For employee data that a Customer loads into its workspace, the Customer decides and we process it on the Customer\'s behalf.',
  ] },
  { h: '2. Data we collect', p: [
    'Signup and account data: name, work email, username, company name, country, and technical data such as IP address, browser and login times.',
    'Employee data entered by a Customer: identity and contact details, employee ID, department and designation, PAN, Aadhaar (where provided), UAN and ESI numbers, bank account details, salary and CTC, attendance and leave, tax declarations and proofs, loans, reimbursements and payslips.',
  ] },
  { h: '3. Why we use it', p: [
    'To create and run workspaces, calculate payroll and statutory deductions, produce payslips, reports, bank files and challans, verify email addresses, secure accounts, prevent fraud and abuse, provide support, and meet legal obligations.',
    'We do not sell personal data and do not use employee data for advertising.',
  ] },
  { h: '4. Legal basis', p: [
    'We process personal data based on the Customer\'s instructions and its lawful purpose as an employer, the contract with the Customer, consent where required, and legal obligations under Indian tax, labour and data protection laws, including the Digital Personal Data Protection Act, 2023.',
  ] },
  { h: '5. Sharing', p: [
    'We share data only with: service providers who host, back up, send email or support the Service under confidentiality and security terms; banks and payment partners when the Customer generates payment files; government or statutory authorities when the Customer files returns or when law requires; and connected systems such as uKnowva HRMS that the Customer enables.',
  ] },
  { h: '6. Storage and security', p: [
    'Data is stored on servers located in India unless agreed otherwise with the Customer. Each workspace has its own database. We use encryption in transit, encrypted secrets, role based access, audit trails, regular backups and access only by authorised staff on a need to know basis.',
  ] },
  { h: '7. Retention', p: [
    'Payroll and tax records are kept for as long as the Customer\'s subscription lasts and as long as needed for statutory record keeping (typically 8 years for tax and payroll records in India). Unverified signups are deleted after the link expires. On termination, data is handled as described in the Terms of Service.',
  ] },
  { h: '8. Your rights', p: [
    'You can ask to access, correct, update or erase your personal data, withdraw consent where processing relies on it, and nominate another person to exercise your rights. Employees should first contact their employer, which controls their payroll data. We will help the employer respond.',
  ] },
  { h: '9. Cookies', p: [
    'We use only the cookies and browser storage needed to keep you signed in, remember your workspace and preferences, and protect the Service. We do not use advertising cookies.',
  ] },
  { h: '10. Data breaches', p: [
    'If a personal data breach affects your data we will notify the Customer without undue delay, and the Data Protection Board of India and affected people where law requires.',
  ] },
  { h: '11. Children', p: [
    'The Service is meant for businesses and is not directed at children. Data of minor employees, where lawfully employed, is processed only as entered by the employer.',
  ] },
  { h: '12. Changes and contact', p: [
    'We may update this policy and will show the new date at the top. For questions, requests or grievances, contact your uKnowva account manager or the support contact in your order form, who will route it to our Grievance Officer. We aim to respond within 30 days.',
  ] },
];

const TITLE: Record<LegalKind, string> = { terms: 'Terms of Service', privacy: 'Privacy Policy' };

function LegalBody({ kind, onSwitch }: { kind: LegalKind; onSwitch: () => void }) {
  const other: LegalKind = kind === 'terms' ? 'privacy' : 'terms';
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {(kind === 'terms' ? TERMS : PRIVACY).map((s) => (
        <Box key={s.h}>
          <Typography sx={{ fontWeight: 700, mb: 0.5 }}>{s.h}</Typography>
          {s.p.map((t, i) => (
            <Typography key={i} variant="body2" color="text.secondary" sx={{ mb: 0.75, lineHeight: 1.6 }}>{t}</Typography>
          ))}
        </Box>
      ))}
      <Typography variant="body2">
        See also the{' '}
        <Link component="button" type="button" onClick={onSwitch} sx={{ verticalAlign: 'baseline' }}>{TITLE[other]}</Link>.
      </Typography>
    </Box>
  );
}

/** The terms or privacy policy in a popup; null kind keeps it closed. `onAgree` adds an I agree button. */
export function LegalDialog({ kind, onChange, onAgree }: {
  kind: LegalKind | null; onChange: (k: LegalKind | null) => void; onAgree?: () => void;
}) {
  return (
    <Dialog open={!!kind} onClose={() => onChange(null)} maxWidth="md" fullWidth scroll="paper">
      {kind && (
        <>
          <DialogTitle>
            {TITLE[kind]}
            <Typography variant="body2" color="text.secondary">uKnowva Payroll · Last updated {UPDATED}</Typography>
            <DialogClose onClose={() => onChange(null)} />
          </DialogTitle>
          <DialogContent dividers>
            <LegalBody kind={kind} onSwitch={() => onChange(kind === 'terms' ? 'privacy' : 'terms')} />
          </DialogContent>
          {onAgree && (
            <DialogActions>
              <Button color="inherit" onClick={() => onChange(null)}>Close</Button>
              <Button variant="contained" onClick={() => { onAgree(); onChange(null); }}>
                I agree to the Terms and Privacy Policy
              </Button>
            </DialogActions>
          )}
        </>
      )}
    </Dialog>
  );
}

export function LegalPage({ kind }: { kind: LegalKind }) {
  const base = window.location.pathname.replace(/\/(terms|privacy)\/?$/, '');
  return (
    <SignupShell title={TITLE[kind]} subtitle={`uKnowva Payroll · Last updated ${UPDATED}`} width={820}>
      <LegalBody kind={kind} onSwitch={() => { window.location.href = `${base}/${kind === 'terms' ? 'privacy' : 'terms'}`; }} />
    </SignupShell>
  );
}
